import { Controller } from "@hotwired/stimulus"

// severity rank lives HERE and only here (single source for strip markers).
const SEV_RANK = { critical: 5, major: 4, moderate: 3, minor: 2, trivial: 1, note: 0 }
const STORAGE_KEY = "ms-studio:rail-filters"

export default class extends Controller {
  static targets = [
    "openTab", "allTab", "filterButton", "filterPopover", "filterPills",
    "body", "group", "card", "openCount", "totalCount", "emptyState",
    "reviewerCheckbox", "minSevButton",
  ]

  connect() {
    this.tab = "open"
    this.reviewers = new Set()
    this.minSev = "all"
    this.restoreFilters()
    this.applyFilters()
    this.dispatchComments()
    this.syncFrameFromLocation()

    this.onFilterOutside = this.onFilterOutside.bind(this)
    this.onFilterEsc = this.onFilterEsc.bind(this)
  }

  disconnect() {
    // The filter popover listeners are document-level; drop them if the
    // controller tears down while the popover is still open.
    document.removeEventListener("mousedown", this.onFilterOutside)
    document.removeEventListener("keydown", this.onFilterEsc)
  }

  // --- Markers (single source) --------------------------------------------

  computeMarkers() {
    const markers = {}
    this.cardTargets.forEach((card) => {
      const frame = card.dataset.frameNumber
      if (!frame || frame === "overall") return
      const n = Number(frame)
      const resolved = card.dataset.resolved === "true"
      const severity = card.dataset.severity || "note"
      const isNote = card.dataset.kind === "note"
      const actioned = card.dataset.actioned === "true"
      const entry = markers[n] || { open: 0, total: 0, worstRank: -1, worst: "note", noteActioned: 0, noteNeedsAction: 0 }
      entry.total += 1
      if (isNote) {
        if (actioned) entry.noteActioned += 1
        else entry.noteNeedsAction += 1
      }
      if (!resolved) {
        entry.open += 1
        const rank = SEV_RANK[severity] ?? 0
        if (rank > entry.worstRank) { entry.worstRank = rank; entry.worst = severity }
      }
      markers[n] = entry
    })
    Object.values(markers).forEach((m) => { if (m.open === 0) m.worst = "resolved" })
    return markers
  }

  dispatchComments() {
    const markers = this.computeMarkers()
    const openCount = this.cardTargets.filter((c) => c.dataset.resolved !== "true").length
    const totalCount = this.cardTargets.length
    if (this.hasOpenCountTarget) this.openCountTarget.textContent = openCount
    if (this.hasTotalCountTarget) this.totalCountTarget.textContent = totalCount
    if (this.hasOpenTabTarget) this.openTabTarget.textContent = `Open ${openCount}`
    if (this.hasAllTabTarget) this.allTabTarget.textContent = `All ${totalCount}`
    this.dispatch("comments-changed", {
      detail: { openCount, totalCount, markers },
      prefix: "animatic", target: window, bubbles: true,
    })
  }

  // --- Tabs / filters ------------------------------------------------------

  setTab(event) {
    this.tab = event.currentTarget.dataset.tab
    if (this.hasOpenTabTarget) this.openTabTarget.classList.toggle("is-active", this.tab === "open")
    if (this.hasAllTabTarget) this.allTabTarget.classList.toggle("is-active", this.tab === "all")
    // tab change clears any pending-resolve exemption
    this.cardTargets.forEach((c) => delete c.dataset.pendingResolve)
    this.applyFilters()
    this.persistFilters()
  }

  toggleReviewer(event) {
    const key = event.currentTarget.value
    if (event.currentTarget.checked) this.reviewers.add(key)
    else this.reviewers.delete(key)
    this.applyFilters()
    this.persistFilters()
  }

  setMinSev(event) {
    this.minSev = event.currentTarget.dataset.minSev
    this.minSevButtonTargets.forEach((b) => b.classList.toggle("is-active", b.dataset.minSev === this.minSev))
    this.applyFilters()
    this.persistFilters()
  }

  clearFilters() {
    this.reviewers.clear()
    this.minSev = "all"
    this.reviewerCheckboxTargets.forEach((cb) => { cb.checked = false })
    this.minSevButtonTargets.forEach((b) => b.classList.toggle("is-active", b.dataset.minSev === "all"))
    this.applyFilters()
    this.persistFilters()
  }

  // "Reset workspace" (from the review controller): drop persisted filters and
  // return to the default Open tab with no reviewer/severity filters. We reset
  // the UI WITHOUT re-persisting, then clear the key, so nothing is left behind.
  resetWorkspace() {
    this.tab = "open"
    this.reviewers.clear()
    this.minSev = "all"
    if (this.hasOpenTabTarget) this.openTabTarget.classList.toggle("is-active", true)
    if (this.hasAllTabTarget) this.allTabTarget.classList.toggle("is-active", false)
    this.reviewerCheckboxTargets.forEach((cb) => { cb.checked = false })
    this.minSevButtonTargets.forEach((b) => b.classList.toggle("is-active", b.dataset.minSev === "all"))
    this.cardTargets.forEach((c) => delete c.dataset.pendingResolve)
    this.applyFilters()
    try { localStorage.removeItem(STORAGE_KEY) } catch (e) { /* ignore */ }
  }

  removePill(event) {
    const kind = event.currentTarget.dataset.pill
    if (kind === "minsev") {
      this.minSev = "all"
      this.minSevButtonTargets.forEach((b) => b.classList.toggle("is-active", b.dataset.minSev === "all"))
    } else {
      this.reviewers.delete(kind)
      const cb = this.reviewerCheckboxTargets.find((c) => c.value === kind)
      if (cb) cb.checked = false
    }
    this.applyFilters()
    this.persistFilters()
  }

  applyFilters() {
    let anyVisible = false
    this.cardTargets.forEach((card) => {
      const resolved = card.dataset.resolved === "true"
      const reviewer = card.dataset.reviewer
      const severity = card.dataset.severity || "note"
      const pending = card.dataset.pendingResolve === "true"
      let hide = false
      if (this.tab === "open" && resolved && !pending) hide = true
      if (this.reviewers.size && !this.reviewers.has(reviewer)) hide = true
      if (this.minSev !== "all" && (SEV_RANK[severity] ?? -1) < SEV_RANK[this.minSev]) hide = true
      card.hidden = hide
      if (!hide) anyVisible = true
    })

    this.groupTargets.forEach((group) => {
      const visibleCards = Array.from(group.querySelectorAll("[data-comment-rail-target~='card']")).some((c) => !c.hidden)
      group.hidden = !visibleCards
    })

    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = anyVisible
    this.renderPills()
  }

  renderPills() {
    if (!this.hasFilterPillsTarget) return
    const pills = []
    this.reviewers.forEach((key) => {
      const cb = this.reviewerCheckboxTargets.find((c) => c.value === key)
      const name = cb?.dataset.reviewerName || key
      pills.push(`<button class="ms-tag" data-pill="${key}" data-action="comment-rail#removePill">${name} ✕</button>`)
    })
    if (this.minSev !== "all") {
      pills.push(`<button class="ms-tag" data-pill="minsev" data-action="comment-rail#removePill">≥ ${this.minSev} ✕</button>`)
    }
    this.filterPillsTarget.innerHTML = pills.join("")
    this.filterPillsTarget.hidden = pills.length === 0
  }

  // --- Filter popover ------------------------------------------------------

  toggleFilter() {
    if (!this.hasFilterPopoverTarget) return
    const open = this.filterPopoverTarget.hidden
    this.filterPopoverTarget.hidden = !open
    if (open) {
      document.addEventListener("mousedown", this.onFilterOutside)
      document.addEventListener("keydown", this.onFilterEsc)
    } else {
      this.closeFilter()
    }
  }

  closeFilter() {
    if (this.hasFilterPopoverTarget) this.filterPopoverTarget.hidden = true
    document.removeEventListener("mousedown", this.onFilterOutside)
    document.removeEventListener("keydown", this.onFilterEsc)
  }

  onFilterOutside(event) {
    if (this.hasFilterPopoverTarget && !this.filterPopoverTarget.contains(event.target) &&
        this.hasFilterButtonTarget && !this.filterButtonTarget.contains(event.target)) {
      this.closeFilter()
    }
  }

  onFilterEsc(event) { if (event.key === "Escape") this.closeFilter() }

  // --- Resolve -------------------------------------------------------------

  async toggleResolve(event) {
    const button = event.currentTarget
    const card = button.closest("[data-comment-rail-target~='card']")
    if (!card) return

    const wasResolved = card.dataset.resolved === "true"
    const nowResolved = !wasResolved

    // optimistic flip + exempt from Open-tab hiding until the request settles
    card.dataset.resolved = nowResolved ? "true" : "false"
    card.dataset.pendingResolve = "true"
    this.paintCard(card)
    this.applyFilters()
    this.dispatchComments()

    const kind = card.dataset.kind
    const body = kind === "note"
      ? { status: nowResolved ? "resolved" : "open" }
      : { resolved: nowResolved }

    try {
      const response = await fetch(card.dataset.resolveUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify(body),
      })
      const ct = response.headers.get("content-type") || ""
      if (!response.ok || !ct.includes("application/json")) throw new Error("bad response")
      await response.json()
      delete card.dataset.pendingResolve
      this.clearCardError(card)
      this.applyFilters()
    } catch (error) {
      // revert
      card.dataset.resolved = wasResolved ? "true" : "false"
      delete card.dataset.pendingResolve
      this.paintCard(card)
      this.showCardError(card)
      this.applyFilters()
      this.dispatchComments()
    }
  }

  paintCard(card) {
    const resolved = card.dataset.resolved === "true"
    card.classList.toggle("is-resolved", resolved)
    const button = card.querySelector("[data-action~='comment-rail#toggleResolve']")
    if (button) button.setAttribute("aria-pressed", resolved ? "true" : "false")
  }

  showCardError(card) {
    let error = card.querySelector(".ms-studio__card-error")
    if (!error) {
      error = document.createElement("p")
      error.className = "ms-studio__card-error"
      card.appendChild(error)
    }
    error.textContent = "Couldn't save — try again"
    error.hidden = false
  }

  clearCardError(card) {
    const error = card.querySelector(".ms-studio__card-error")
    if (error) error.hidden = true
  }

  // --- Seek + now-playing --------------------------------------------------

  seek(event) {
    const frameNumber = Number(event.currentTarget.dataset.frameNumber)
    if (!Number.isFinite(frameNumber)) return
    this.dispatch("seek", {
      detail: { frameNumber },
      prefix: "animatic", target: window, bubbles: true,
    })
  }

  frameChanged(event) {
    const frameNumber = Number(event.detail?.frameNumber)
    if (!Number.isFinite(frameNumber)) return
    this.currentFrameNumber = frameNumber
    let currentGroup = null
    this.groupTargets.forEach((group) => {
      const isCurrent = Number(group.dataset.frameGroup) === frameNumber
      if (isCurrent) currentGroup = group
      const pill = group.querySelector(".ms-studio__now-playing")
      if (pill) pill.hidden = !isCurrent
      const chip = group.querySelector(".ms-studio__group-chip")
      if (chip) chip.classList.toggle("is-current", isCurrent)
    })
    this.cardTargets.forEach((card) => {
      card.classList.toggle("is-on-frame", Number(card.dataset.frameNumber) === frameNumber)
    })
    this.scrollCurrentGroupIntoView(currentGroup)
  }

  syncFrameFromLocation() {
    const frameNumber = Number(new URL(window.location.href).searchParams.get("frame") || 1)
    if (!Number.isFinite(frameNumber)) return

    window.requestAnimationFrame(() => {
      this.frameChanged({ detail: { frameNumber } })
    })
  }

  scrollCurrentGroupIntoView(group) {
    if (!group || group.hidden || !this.hasBodyTarget) return

    window.requestAnimationFrame(() => {
      if (!this.hasBodyTarget || group.hidden || !this.bodyTarget.contains(group)) return

      const body = this.bodyTarget
      const bodyRect = body.getBoundingClientRect()
      const groupRect = group.getBoundingClientRect()
      const targetTop = groupRect.top - bodyRect.top + body.scrollTop - 8
      const maxTop = Math.max(0, body.scrollHeight - body.clientHeight)
      const top = Math.max(0, Math.min(targetTop, maxTop))
      body.scrollTop = top
    })
  }

  // --- Evidence disclosure -------------------------------------------------

  toggleDisclosure(event) {
    const button = event.currentTarget
    const panels = button.parentElement.querySelector(".ms-studio__disclosure-panels")
    if (!panels) return
    const open = panels.hidden
    panels.hidden = !open
    button.querySelector(".is-label").textContent = open ? "Hide" : "Evidence · suggested change"
  }

  // --- Persistence ---------------------------------------------------------

  persistFilters() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        tab: this.tab, reviewers: [...this.reviewers], minSev: this.minSev,
      }))
    } catch (e) { /* ignore */ }
  }

  restoreFilters() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")
      if (saved.tab === "open" || saved.tab === "all") this.tab = saved.tab
      if (Array.isArray(saved.reviewers)) this.reviewers = new Set(saved.reviewers)
      if (typeof saved.minSev === "string") this.minSev = saved.minSev
    } catch (e) { /* ignore */ }
    if (this.hasOpenTabTarget) this.openTabTarget.classList.toggle("is-active", this.tab === "open")
    if (this.hasAllTabTarget) this.allTabTarget.classList.toggle("is-active", this.tab === "all")
    this.reviewerCheckboxTargets.forEach((cb) => { cb.checked = this.reviewers.has(cb.value) })
    this.minSevButtonTargets.forEach((b) => b.classList.toggle("is-active", b.dataset.minSev === this.minSev))
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
